import useWindowSize from "src/hooks/useWindowSize";
import DesktopSearchBar from "./DesktopSearchBar";
import MobileSearchBar from "./MobileSearchBar";
import { useTranslation } from "react-i18next";
import { IoSearchOutline } from "react-icons/io5";
import { RxMixerHorizontal } from "react-icons/rx";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Skeleton } from "antd";

const SearchBar = ({
  filters,
  filterData,
  mainTitle,
  isLoading,
  setOpenFilter,
  totalOffers,
  setOpenMap,
  loadSearchBar,
  activeFiltersCount,
}) => {
  const [isStickyBarVisible, setIsStickyBarVisible] = useState(false);
  const [openMobileSearchBar, setOpenMobileSearchBar] = useState(false);

  const { width } = useWindowSize();
  const { t, i18n } = useTranslation("common");
  const isEn = i18n.language === "en";
  const isMobile = width <= 768;

  const sports = [
    {
      id: "0",
      title: t("frontpage.all_sports"),
      alias: "0",
      type: "sport",
    },
    {
      id: "3",
      title: t("search.searchbar_sports_option_1"),
      alias: "surf",
      type: "sport",
    },
    {
      id: "2",
      title: t("search.searchbar_sports_option_2"),
      alias: "yoga",
      type: "sport",
    },
    {
      id: "5",
      title: t("search.searchbar_sports_option_3"),
      alias: "fitness",
      type: "sport",
    },
    {
      id: "4",
      title: t("search.searchbar_sports_option_4"),
      alias: "hiking",
      type: "sport",
    },
    {
      id: "210",
      title: t("search.searchbar_sports_option_5"),
      alias: "klettern-bouldern-2",
      type: "sport",
    },
    {
      id: "222",
      title: t("search.searchbar_sports_option_6"),
      alias: "bunter-aktivmix",
      type: "sport",
    },
    {
      id: "6",
      title: t("search.searchbar_sports_option_7"),
      alias: "martial-arts",
      type: "sport",
    },
    {
      id: "219",
      title: t("search.searchbar_sports_option_8"),
      alias: "kajak-kanu",
      type: "sport",
    },
    {
      id: "211",
      title: t("search.searchbar_sports_option_9"),
      alias: "fahrrad-mountainbike",
      type: "sport",
    },
    {
      id: "231",
      title: t("search.searchbar_sports_option_10"),
      alias: "kitesurfen",
      type: "sport",
    },
    {
      id: "194",
      title: t("search.searchbar_sports_option_11"),
      alias: "winter-sports",
      type: "sport",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      // Check the scroll position
      if (window.scrollY > 200) {
        setIsStickyBarVisible(true);
      } else {
        setIsStickyBarVisible(false);
      }
    };

    // Add the scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="w-full relative min-h-[150px]">
      <span className="w-screen bg-[#fad49d] h-[75px] absolute top-0 right-0 left-0" />

      {isMobile ? (
        <div className="w-full">
          {/* sticky bar */}
          {isStickyBarVisible && (
            <motion.div
              className="w-full bg-white fixed top-0 z-[99] shadow-md"
              initial={{ opacity: 0.1, y: -100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0,
                duration: 0.1,
                type: "spring",
                stiffness: 300,
                damping: 50,
              }}
            >
              <div className="py-3 container px-4 mx-auto flex items-center justify-between gap-2">
                <div
                  onClick={() => setOpenMobileSearchBar(true)}
                  className="w-[60%] py-2 px-3 rounded-3xl border-2 border-green flex items-center gap-x-2 cursor-pointer"
                >
                  <div>
                    <IoSearchOutline className="!text-[22px]" />
                  </div>
                  <p className="text-sm font-medium line-clamp-1">
                    {isEn
                      ? mainTitle?.en ?? "Sport & active journeys"
                      : mainTitle?.de ?? "Sport- & Aktivreisen"}
                  </p>
                </div>

                <div
                  className={`px-5 py-[10px] bg-[#e3f9fc] rounded-full flex items-center gap-2 cursor-pointer relative ${
                    activeFiltersCount > 0 ? "!border !border-green" : ""
                  }`}
                  onClick={() => setOpenFilter(true)}
                >
                  {activeFiltersCount > 0 && (
                    <span className="absolute px-[7px] py-[0.5px] rounded-full bg-green text-white text-[12px] -top-[7px] right-0 font-medium">
                      {activeFiltersCount}
                    </span>
                  )}
                  <RxMixerHorizontal className="text-[22px]" />
                  <p className="text-base font-medium whitespace-nowrap">
                    {t("offer.open_drawer_filter")}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <MobileSearchBar
            open={openMobileSearchBar}
            setOpen={setOpenMobileSearchBar}
            sports={sports}
            isLoading={isLoading}
            mainTitle={mainTitle}
            filters={filters}
            filterData={filterData}
            totalOffers={totalOffers}
            setOpenFilter={setOpenFilter}
            setOpenMap={setOpenMap}
            activeFiltersCount={activeFiltersCount}
          />
        </div>
      ) : loadSearchBar ? (
        <DesktopSearchBar
          sports={sports}
          filters={filters}
          filterData={filterData}
        />
      ) : (
        <div className="w-full flex flex-col items-center justify-center container">
          <div className="w-full lg:w-[912px] h-[75px] rounded-full bg-gray-100 !z-[100] py-2 px-4 md:pl-8 flex items-center justify-between relative !border-[3px] !border-green mt-[37.5px] shadow-sm">
            <div className="w-full h-full grid grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={"search-bar-placeholder-" + item}
                  className={`w-full flex flex-col items-start justify-start gap-2 pt-2
                    ${
                      item === 1
                        ? "border-r"
                        : item === 2
                        ? "border-r pl-2"
                        : "pl-2"
                    }
                    `}
                >
                  <Skeleton.Button active className="!h-[15px] !w-[70%]" />
                  <Skeleton.Button active className="!h-[15px] !w-[95%]" />
                </div>
              ))}
            </div>

            <div className="relative w-[100px] h-full">
              <div className="absolute !-top-[30px] !-right-7 scale-75">
                <div className="w-full he-full relative">
                  {/* orange */}
                  <div className="scale-95 orange_loading_container">
                    <div className="orange_loading_inner">
                      <div />
                      <div>
                        <div />
                      </div>
                    </div>
                  </div>
                  {/* blue */}
                  <div className="absolute top-[32px] left-[28%] scale-[1.4] blue_loading_container">
                    <div className="blue_loading_inner">
                      <div />
                      <div>
                        <div />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
